import { saveAs } from "file-saver";
import JSZip from "jszip";

import { useEffect, useMemo, useRef } from "react";
import { AxiosError } from "axios";
import { downloadDocument } from "./v2/requests";
import { type DocumentSummary } from "./types";
import i18n from "~/i18n";
import { getNoTierLabel } from "~/helpers/tier";

export const Utils = {
  saveZipDocuments: async (
    documents: DocumentSummary[],
    updateProgress: (value: number) => void,
  ) => {
    const zip = new JSZip();
    let count = 0;

    for (const doc of documents) {
      const version = doc.latestPublishedVersion ?? doc.latestVersion;
      try {
        const blob = await downloadDocument(version.id);

        zip
          .folder(`${doc.category.title}`)
          ?.folder(doc.tier?.title ?? getNoTierLabel(i18n.t))
          ?.file(`${version.title}_${version.id}.${version.fileType}`, blob);
      } catch (error) {
        if (error instanceof AxiosError && error.code !== "ERR_BAD_REQUEST") {
          // Ignore bad request error for now. They are caused by the document being empty.
          throw error;
        }
      }
      count++;
      updateProgress(count);
    }
    await zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, "documents");
    });
  },
};

/**
 * Debounce a callback function
 */
export function useDebounce<
  Callback extends (...args: Parameters<Callback>) => ReturnType<Callback>,
>(callback: Callback, delay: number) {
  const callbackRef = useRef(callback);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Create a stable debounced function wrapper
  return useMemo(() => {
    return (...args: Parameters<Callback>) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // Access ref in the timeout callback, not during render
        callbackRef.current(...args);
      }, delay);
    };
  }, [delay]);
}
